import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageContent } from '@/shared/widgets/layout/PageContent';
import { toast } from '@/shared/hooks/use-toast';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import type { CreateLearningPayload } from '@/features/learnings/service/learningService';
import { ContentBlock } from './LearningForm/content-blocks/ContentBlock';
import { BasicInfoSection } from './LearningForm/sections/BasicInfoSection';
import { CoverSection } from './LearningForm/sections/CoverSection';
import { FormFooter } from '@/shared/widgets/form-actions';
import { SimpleButton } from '@/shared/widgets/feedback';
import { useLearningForm } from '../hooks/useLearningForm';
import { useContentsManager } from '../hooks/useContentsManager';
import { useLearningMutations } from '../hooks/useLearningMutations';
import { useLearningData } from '../hooks/useLearningData';
import { DeleteContentModal } from './DeleteContentModal';

const LearningEdit: React.FC = () => {
    const { id } = useParams();
    const isEditing = Boolean(id);
    const { state } = useLocation();
    const navigate = useNavigate();
    const learning = state?.learning;
    const { formData, setFormData, coverFile, handleCoverUpload } = useLearningForm(learning);
    const { contents, addContent, removeContent, updateContent, moveContent, setContents } = useContentsManager();
    const { createMutation, updateMutation, deleteMutation, associateCategory, removeCategory } = useLearningMutations();
    const { creators, isLoadingCreators, learningsContents, isLoadingLearningsContents } = useLearningData(id, isEditing);

    // Estado de validação manual
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Estado do modal de delete
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // Função de validação
    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.name?.trim()) {
            errors.name = "O título é obrigatório";
        } else if (formData.name.trim().length < 3) {
            errors.name = "O título deve ter pelo menos 3 caracteres";
        }

        if (!formData.creatorId) {
            errors.creatorId = "Selecione um criador";
        }

        if (!formData.cover && !coverFile) {
            errors.cover = "Uma imagem de capa é obrigatória";
        }

        if (!formData.categoryLearning) {
            errors.categoryLearning = "Uma categoria é obrigatória";
        }

        if (!contents || contents.length === 0) {
            errors.contents = "Adicione pelo menos um conteúdo";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Função para limpar erro de campo específico
    const clearFieldError = (field: string) => {
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
        });
    };

    // Função para deletar o learning
    const handleDeleteLearning = async () => {
        if (!id) return;

        try {
            await deleteMutation.mutateAsync({ id });
            toast({
                title: "Conteúdo deletado",
                description: "O conteúdo foi deletado com sucesso.",
                variant: "success",
            });
            navigate('/learning');
        } catch (error) {
            toast({
                title: "Erro ao deletar conteúdo",
                description: error instanceof Error ? error.message : "Ocorreu um erro inesperado.",
                variant: "destructive",
            });
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        // Validar formulário antes de submeter
        if (!validateForm()) {
            toast({
                title: "Campos obrigatórios",
                description: "Por favor, corrija os erros destacados antes de continuar.",
                variant: "destructive",
            });
            return;
        }


        const selectedCategoryId = formData.categoryLearning;

        const payload: CreateLearningPayload = {
            name: formData.name,
            type: formData.type,
            creatorId: formData.creatorId,
            coverType: formData.coverType,
            published: formData.published,
            contents: contents.map(content => {
                const baseContent = {
                    type: content.type,
                    subType: content.subType,
                    value: content.value,
                    order: content.order,
                    published: content.published
                };

                // Para vídeos, incluir title e description
                if (content.type === 'VIDEO') {
                    return {
                        ...baseContent,
                        title: content.title || '',
                        description: content.description || '',
                        provider: content.provider || 'YOUTUBE'
                    };
                }

                // Para updates, preservar campos que não devem ser sobrescritos
                if (isEditing) {
                    return {
                        ...baseContent,
                        displayOrder: content.displayOrder,
                        highlightOrder: content.highlightOrder
                    };
                }

                return baseContent;
            }),
        };

        try {
            const cover = coverFile ?? undefined;

            if (isEditing && id) {
                // 1. Atualizar o learning
                await updateMutation.mutateAsync({
                    id,
                    data: payload,
                    cover
                });

                // 2. Gerenciar categoria se houve mudança
                const currentCategoryId = learning?.categoryId || learning?.category?.id;
                const newCategoryId = formData.categoryLearning;

                if (currentCategoryId !== newCategoryId) {
                    // Se havia categoria e mudou, remove a antiga
                    if (currentCategoryId) {
                        await removeCategory.mutateAsync({ learningId: id });
                    }

                    // Se há nova categoria, associa
                    if (newCategoryId) {
                        await associateCategory.mutateAsync({
                            learningId: id,
                            categoryId: newCategoryId
                        });
                    }
                }

                toast({
                    title: "Sucesso!",
                    description: "Conteúdo atualizado com sucesso.",
                    variant: "default",
                });
                navigate('/learning');
            } else {
                const createdLearning = await createMutation.mutateAsync({
                    ...payload,
                    cover,
                });

                await associateCategory.mutateAsync({
                    learningId: createdLearning.id,
                    categoryId: selectedCategoryId,
                });

                toast({
                    title: "Sucesso!",
                    description: "Conteúdo criado com sucesso.",
                    variant: "default",
                });
                navigate('/learning');
            }
        } catch (error) {
            toast({
                title: "Erro",
                description: "Ocorreu um erro inesperado. Tente novamente.",
                variant: "destructive",
            });
            console.error('Error submitting form:', error);
        }
    };

    useEffect(() => {
        if (isEditing && id && !isLoadingLearningsContents && learningsContents) {
            const apiContents = Array.isArray(learningsContents)
                ? learningsContents
                : (learningsContents as any)?.data;

            if (apiContents && apiContents.length > 0) {
                const sortedContents = [...apiContents].sort((a, b) => a.order - b.order);
                setContents(sortedContents);
            }
        }
    }, [isEditing, id, isLoadingLearningsContents, learningsContents]);

    useEffect(() => {
        // Usar dados da API se disponíveis, caso contrário usar dados do state

        if (isEditing && learning) {
            console.log('Carregando dados do learning:', learning);
            setFormData(prev => ({
                ...prev,
                name: learning.name || '',
                type: learning.type || 'ARTICLE',
                creatorId: learning.creatorId || '',
                cover: learning.cover || '',
                coverType: learning.coverType || 'FOTO',
                published: learning.published || false,
                categoryLearning: learning.categoryLearning,
            }));
        }
    }, [isEditing, learning]);

    return (
        <PageContent
            title={isEditing ? "Editar Conteúdo" : "Novo Conteúdo"}
            items={[
                { label: "Início", href: "/" },
                { label: "Conteúdos", href: "/learning" },
                { label: isEditing ? "Editar" : "Novo", href: "/learning/new" },
            ]}
        >
            {/* Alert de validação */}
            {Object.keys(validationErrors).length > 0 && (
                <Alert variant="destructive" className="mb-6">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        Existem {Object.keys(validationErrors).length} erro(s) que precisam ser corrigidos antes de salvar.
                    </AlertDescription>
                </Alert>
            )}

            <form className="space-y-6">

                <BasicInfoSection
                    formData={formData}
                    setFormData={setFormData}
                    validationErrors={validationErrors}
                    clearFieldError={clearFieldError}
                />
                <CoverSection
                    formData={formData}
                    setFormData={setFormData}
                    handleCoverUpload={handleCoverUpload}
                    id={id || ""}
                    isLoadingCreators={isLoadingCreators}
                    creators={creators}
                    validationErrors={validationErrors}
                    clearFieldError={clearFieldError}
                />
                <ContentBlock
                    contents={contents ?? []}
                    addContent={addContent}
                    updateContent={updateContent}
                    removeContent={removeContent}
                    moveContent={moveContent}
                    validationErrors={validationErrors}
                    clearFieldError={clearFieldError}
                    learningType={formData.type}
                />
                <FormFooter
                    isLoading={createMutation.isPending || updateMutation.isPending || deleteMutation.isPending}
                    isSuccess={createMutation.isSuccess || updateMutation.isSuccess}
                >
                    <div className="flex gap-3 justify-between w-full">
                        {isEditing && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsDeleteModalOpen(true)}
                                disabled={createMutation.isPending || updateMutation.isPending}
                                className="bg-transparent border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/70"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Deletar Conteúdo
                            </Button>
                        )}
                        <div className="flex gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => window.history.back()}
                                className="bg-transparent border-[#333333] text-white hover:bg-[#222222] hover:text-white/60"
                                disabled={createMutation.isPending || updateMutation.isPending || deleteMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <SimpleButton
                                onClick={handleSubmit}
                                isLoading={createMutation.isPending || updateMutation.isPending}
                                isSuccess={createMutation.isSuccess || updateMutation.isSuccess}
                                loadingText="Salvando..."
                                successText={isEditing ? "Conteúdo atualizado!" : "Conteúdo criado!"}
                                successDuration={3000}
                            >
                                {isEditing ? 'Atualizar Conteúdo' : 'Criar Conteúdo'}
                            </SimpleButton>
                        </div>
                    </div>
                </FormFooter>
            </form>

            <DeleteContentModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteLearning}
                contentType="conteúdo"
                isLoading={deleteMutation.isPending}
            />
        </PageContent>
    );
};

export default LearningEdit;
